import React, { useMemo, useState } from "react";
import { Autocomplete, TextField, Stack, Skeleton } from "@mui/material";

import { User } from "../../types";
import { useListOrganisations, useListUsers } from "../../store";
import { isValidEmail } from "../meeting-management/common/form-utils";
import {
  createTeamDictionary,
  createUserDictionary,
} from "../../utils/create-dictionary";
import AttendeeOption from "./attendee-option";
import AttendeeList from "./attendee-list";

export interface AttendeeItem {
  id: string;
  name: string;
  type: "user" | "invite" | "team";
  user?: User;
  groupLabel: string;
}

interface AttendeesSelectProps {
  selectedUserIds: string[];
  selectedInviteEmails: string[];
  selectedTeamsIds: string[];
  allowEmptyTeams?: boolean;
  lockedUserIds?: string[];
  lockedInviteEmails?: string[];
  lockedTeamIds?: string[];
  disabled?: boolean;
  onUsersChange: (userIds: string[]) => void;
  onInviteEmailsChange: (emails: string[]) => void;
  onTeamChange: (teamIds: string[]) => void;
  error?: string | string[] | undefined;
}

const AttendeesSelect: React.FC<AttendeesSelectProps> = ({
  selectedUserIds,
  selectedInviteEmails,
  selectedTeamsIds,
  allowEmptyTeams,
  lockedUserIds = [],
  lockedInviteEmails = [],
  lockedTeamIds = [],
  disabled,
  onUsersChange: onUChange,
  onInviteEmailsChange,
  onTeamChange,
  error,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [localError, setLocalError] = useState<string | undefined>();

  const { data: users, isLoading: loadingUsers } = useListUsers();
  const { data: teams, isLoading: loadingTeams } = useListOrganisations();

  const teamDictionary = useMemo(() => {
    return createTeamDictionary(teams);
  }, [teams]);

  const isLoading = loadingUsers || loadingTeams;

  const userMap = useMemo(() => {
    return createUserDictionary(users);
  }, [users]);

  const knownUsersByEmail = useMemo(() => {
    return (users || []).reduce<Record<string, User>>((acc, user) => {
      if (user.email) {
        acc[user.email.trim().toLowerCase()] = user;
      }
      return acc;
    }, {});
  }, [allowEmptyTeams, teamDictionary, teams, users]);

  const onUsersChange = (userIds: string[]) => {
    if (allowEmptyTeams) {
      onUChange(userIds);
      return;
    }

    const allowedOrgs: string[] = [];
    for (let userId of userIds) {
      const user = userMap[userId];

      if (!user) {
        continue;
      }

      for (let teamId of user.organisationIds) {
        if (!allowedOrgs.includes(teamId)) {
          allowedOrgs.push(teamId);
        }
      }
    }

    const updatedTeams = selectedTeamsIds.filter((item) =>
      allowedOrgs.includes(item),
    );

    if (updatedTeams.length != selectedTeamsIds.length) {
      onTeamChange(updatedTeams);
    }

    onUChange(userIds);
  };

  const optionSelected = (item?: AttendeeItem) => {
    if (!item?.id) {
      return false;
    }

    if (item?.type == "invite") {
      return selectedInviteEmails.includes(item.id);
    }

    if (item?.type == "user") {
      return selectedUserIds.includes(item.id);
    }

    if (item?.type == "team") {
      return selectedTeamsIds.includes(item.id);
    }

    return false;
  };

  const addInviteEmail = (rawValue: string): boolean => {
    const email = rawValue.trim();

    if (!email) return false;

    if (!isValidEmail(email)) {
      const message = "Enter a valid email address.";
      setLocalError(message);
      return false;
    }

    const normalizedEmail = email.toLowerCase();
    const matchedUser = knownUsersByEmail[normalizedEmail];

    if (matchedUser) {
      if (!selectedUserIds.includes(matchedUser.userId)) {
        onUsersChange([...selectedUserIds, matchedUser.userId]);
      }
      setLocalError(undefined);
      return true;
    }

    if (
      selectedInviteEmails.some(
        (value) => value.toLowerCase() === normalizedEmail,
      )
    ) {
      setLocalError(undefined);
      return true;
    }

    onInviteEmailsChange([...selectedInviteEmails, email]);
    setLocalError(undefined);
    return true;
  };

  const options = useMemo(() => {
    const items: AttendeeItem[] = [];

    (users || []).forEach((user) => {
      if (!user) {
        return;
      }

      items.push({
        id: user.userId,
        name: user.fullName || user.email || "Unnamed user",
        type: "user",
        user,
        groupLabel: "Users",
      });
    });

    // if (allowEmptyTeams) {
    //     (teams || []).forEach((team) => {
    //         if (!team) {
    //             return;
    //         }

    //         items.push({
    //             id: team.organisationId,
    //             name: team.name,
    //             type: "team",
    //             groupLabel: "Teams",
    //         });
    //     });
    // }

    return items.sort((a, b) => {
      if (a.groupLabel !== b.groupLabel) {
        return a.groupLabel === "Users" ? -1 : 1;
      }

      return a.name.localeCompare(b.name);
    });
  }, [allowEmptyTeams, teams, users]);

  const filterOptions = (
    items: AttendeeItem[],
    { inputValue: searchValue }: { inputValue: string },
  ): AttendeeItem[] => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    if (!normalizedSearch) return items;

    return items.filter((item) => {
      if (item.type === "user") {
        const fullNameMatches = item.user?.fullName
          ?.toLowerCase()
          .includes(normalizedSearch);
        const emailMatches = item.user?.email
          ?.toLowerCase()
          .includes(normalizedSearch);

        return Boolean(fullNameMatches || emailMatches);
      }

      if (item.type === "team") {
        const team = teamDictionary[item.id];
        const fullNameMatches = team?.name
          ?.toLowerCase()
          .includes(normalizedSearch);
        return fullNameMatches;
      }

      return item.name.toLowerCase().includes(normalizedSearch);
    });
  };

  const handleAutocompleteChange = (
    _event: any,
    value: Array<AttendeeItem | string>,
  ) => {
    const newItem = value[value.length - 1];
    if (!newItem) return;

    if (typeof newItem === "string") {
      const didAdd = addInviteEmail(newItem);
      if (didAdd) setInputValue("");
      return;
    }

    if (newItem.type === "user") {
      if (selectedUserIds.includes(newItem.id)) {
        setLocalError(undefined);
        setInputValue("");
        return;
      }

      onUsersChange([...selectedUserIds, newItem.id]);
      setLocalError(undefined);
      setInputValue("");
    }

    if (newItem.type === "team") {
      if (selectedTeamsIds.includes(newItem.id)) {
        setLocalError(undefined);
        setInputValue("");
        return;
      }

      onTeamChange([...selectedTeamsIds, newItem.id]);
      setLocalError(undefined);
      setInputValue("");
    }
  };

  const commitInputValue = () => {
    if (!inputValue.trim()) return;

    const didAdd = addInviteEmail(inputValue);
    if (didAdd) setInputValue("");
  };

  const getOptionLabel = (option: string | AttendeeItem) => {
    return typeof option === "string" ? option : option.name;
  };

  const effectiveError = localError || error;

  const selectedUsers = useMemo(() => {
    return selectedUserIds
      .map((userId) => userMap[userId])
      .filter((user): user is User => !!user)
      .map((user) => ({
        userId: user.userId,
        label: user.fullName || user.email || "Unnamed user",
        teams: (user.organisationIds || [])
          .map((teamId) => teamDictionary[teamId])
          .filter((team): team is NonNullable<typeof team> => !!team)
          .map((team) => ({
            teamId: team.organisationId,
            name: team.name,
            color: team.color,
          })),
      }));
  }, [teamDictionary, selectedUserIds, userMap]);

  const selectedTeams = useMemo(() => {
    return selectedTeamsIds
      .map((teamId) => teamDictionary[teamId])
      .filter((team): team is NonNullable<typeof team> => !!team)
      .map((team) => ({
        teamId: team.organisationId,
        name: team.name,
        color: team.color,
      }));
  }, [teamDictionary, selectedTeamsIds]);

  return (
    <Stack spacing={2}>
      {isLoading && <Skeleton height={"80px"} />}
      {!isLoading && (
        <Stack spacing={1} sx={{ mt: 2 }}>
          <AttendeeList
            selectedUsers={selectedUsers}
            selectedInviteEmails={selectedInviteEmails}
            selectedTeams={selectedTeams}
            editable
            lockedUserIds={lockedUserIds}
            lockedInviteEmails={lockedInviteEmails}
            lockedTeamIds={lockedTeamIds}
            onRemoveUser={(userId) => {
              onUsersChange(selectedUserIds.filter((id) => id !== userId));
            }}
            onRemoveInviteEmail={(inviteEmail) => {
              onInviteEmailsChange(
                selectedInviteEmails.filter((email) => email !== inviteEmail),
              );
            }}
            onRemoveTeam={(teamId) => {
              onTeamChange(selectedTeamsIds.filter((id) => id !== teamId));
            }}
          />
        </Stack>
      )}

      <Autocomplete<AttendeeItem, true, false, true>
        multiple
        freeSolo
        options={options}
        value={[]}
        onChange={handleAutocompleteChange}
        inputValue={inputValue}
        onInputChange={(_event, value, reason) => {
          if (reason === "reset") return;
          setInputValue(value);
        }}
        getOptionLabel={getOptionLabel}
        renderOption={(props, option) => (
          <React.Fragment key={option.id}>
            <AttendeeOption
              liProps={props}
              option={option}
              teamDictionary={teamDictionary}
              optionSelected={optionSelected}
            />
          </React.Fragment>
        )}
        filterOptions={filterOptions}
        loading={isLoading}
        disabled={disabled}
        filterSelectedOptions
        getOptionDisabled={optionSelected}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        // groupBy={(option) => option.groupLabel === "Users" ? "Users" : "Teams"}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label="Add Attendees"
            placeholder="Add people using their name or email"
            error={!!effectiveError}
            helperText={
              Array.isArray(effectiveError)
                ? effectiveError.join(", ")
                : effectiveError
            }
            onBlur={commitInputValue}
            onKeyDown={(event) => {
              if (
                event.key === "Enter" ||
                event.key === "," ||
                event.key === "Tab"
              ) {
                if (!inputValue.trim()) return;
                event.preventDefault();
                commitInputValue();
              }
            }}
          />
        )}
        sx={{
          minWidth: 300,
          "& .MuiOutlinedInput-root": {
            minHeight: 36,
          },
          "& .MuiAutocomplete-input": {
            py: 0.5,
          },
        }}
      />
    </Stack>
  );
};

export default AttendeesSelect;
